/**
 * The deck editor's right-click menu and keyboard shortcuts — the usual ones from Keynote, Figma and Canva:
 * ⌘X ⌘C ⌘V ⌘D, ⌫, ⌘] / ⌘[ (forward / backward), ⇧⌘] / ⇧⌘[ (front / back), ⌥A ⌥H ⌥D (left / centre / right),
 * ⌥W ⌥V ⌥S (top / middle / bottom). Both call the selection's owner (canvasCommands.ts).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlignCenterHorizontal, AlignCenterVertical, AlignEndHorizontal, AlignEndVertical, AlignStartHorizontal, AlignStartVertical, ArrowDown, ArrowUp, BringToFront, ClipboardPaste, Copy, CopyPlus, Group, Scissors, SendToBack, Trash2 } from 'lucide-react';
import { canvasClip, canvasOwner, isMac, type Align } from './canvasCommands';

const MOD = isMac ? '⌘' : 'Ctrl+';
const ALT = isMac ? '⌥' : 'Alt+';
const SHIFT = isMac ? '⇧' : 'Shift+';
const ALIGN_KEYS: Record<string, Align> = { KeyA: 'left', KeyH: 'center', KeyD: 'right', KeyW: 'top', KeyV: 'middle', KeyS: 'bottom' };

const typing = (t: EventTarget | null) => { const el = t as HTMLElement | null; return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)); };

/** The shortcuts, while `active` (the editor is open and editable). */
export function useCanvasKeys(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      // (a text being edited inside a shadow root shows up as the host here, so look at the real target too)
      if (typing(e.target) || typing(e.composedPath()[0] ?? null)) return;
      const owner = canvasOwner.current; const mod = e.metaKey || e.ctrlKey; const k = e.key.toLowerCase();
      if (mod && !e.shiftKey && !e.altKey && k === 'v') { e.preventDefault(); canvasOwner.paste?.(); return; }
      if (!owner) return;
      if (mod && !e.altKey && k === 'c' && !window.getSelection()?.toString()) { e.preventDefault(); owner.copy(); }
      else if (mod && !e.altKey && k === 'x') { e.preventDefault(); owner.cut(); }
      else if (mod && !e.altKey && k === 'd') { e.preventDefault(); owner.duplicate(); }
      else if (mod && (e.code === 'BracketRight' || e.code === 'BracketLeft')) { e.preventDefault(); owner.order(e.code === 'BracketRight' ? (e.shiftKey ? 'front' : 'forward') : (e.shiftKey ? 'back' : 'backward')); }
      else if (e.altKey && !mod && ALIGN_KEYS[e.code]) { e.preventDefault(); owner.align(ALIGN_KEYS[e.code]); }
      else if (!mod && (e.key === 'Backspace' || e.key === 'Delete')) { e.preventDefault(); owner.remove(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active]);
}

/** The menu: opened by `openCanvasMenu(x, y)` (or the editor's own right-click) at the pointer. */
export function CanvasMenu({ active }: { active: boolean }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => {
    const open = (e: Event) => { if (!active) return; const d = (e as CustomEvent<{ x: number; y: number }>).detail; setPos(null); setAt(d); };
    window.addEventListener('canvas-menu', open); return () => window.removeEventListener('canvas-menu', open);
  }, [active]);
  useEffect(() => {
    if (!at) return;
    const close = (e: Event) => { if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Escape') return; if (e.type === 'pointerdown' && ref.current?.contains(e.target as Node)) return; setAt(null); };
    window.addEventListener('pointerdown', close, true); window.addEventListener('keydown', close, true); window.addEventListener('scroll', close, true); window.addEventListener('resize', close);
    return () => { window.removeEventListener('pointerdown', close, true); window.removeEventListener('keydown', close, true); window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [at]);
  // kept inside the window
  useLayoutEffect(() => {
    if (!at || !ref.current) return;
    const { offsetWidth: w, offsetHeight: h } = ref.current;
    setPos({ left: Math.min(at.x, window.innerWidth - w - 8), top: Math.min(at.y, window.innerHeight - h - 8) });
  }, [at]);
  if (!at) return null;
  const owner = canvasOwner.current;
  const run = (fn?: () => void) => () => { setAt(null); fn?.(); };
  const item = 'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] text-[#1c1917] hover:bg-[#f5f5f4] disabled:pointer-events-none disabled:opacity-40';
  const keys = 'ml-auto pl-6 text-[11.5px] tracking-wide text-[#a8a29e]';
  const sep = <div className="my-1 h-px bg-[#f0efec]" />;
  const Row = ({ icon: I, label, shortcut, onClick, disabled, danger }: { icon: typeof Copy; label: string; shortcut?: string; onClick?: () => void; disabled?: boolean; danger?: boolean }) => (
    <button role="menuitem" className={`${item} ${danger ? 'text-[#b42318] hover:bg-[#fef3f2]' : ''}`} onClick={run(onClick)} disabled={disabled}><I className="h-3.5 w-3.5 shrink-0 opacity-70" />{label}{shortcut && <span className={keys}>{shortcut}</span>}</button>
  );
  const aligns: Array<{ a: Align; icon: typeof Copy; label: string; key: string }> = [
    { a: 'left', icon: AlignStartVertical, label: 'Align left', key: 'A' }, { a: 'center', icon: AlignCenterVertical, label: 'Align centre', key: 'H' }, { a: 'right', icon: AlignEndVertical, label: 'Align right', key: 'D' },
    { a: 'top', icon: AlignStartHorizontal, label: 'Align top', key: 'W' }, { a: 'middle', icon: AlignCenterHorizontal, label: 'Align middle', key: 'V' }, { a: 'bottom', icon: AlignEndHorizontal, label: 'Align bottom', key: 'S' },
  ];
  return createPortal(
    <div ref={ref} role="menu" aria-label="Edit" onContextMenu={(e) => e.preventDefault()}
      style={{ position: 'fixed', left: pos?.left ?? at.x, top: pos?.top ?? at.y, zIndex: 2000, visibility: pos ? 'visible' : 'hidden' }}
      className="w-60 rounded-xl border border-[#e7e5e4] bg-white p-1 shadow-[0_8px_30px_rgba(28,25,23,0.14)]">
      <Row icon={Scissors} label="Cut" shortcut={`${MOD}X`} onClick={owner?.cut} disabled={!owner} />
      <Row icon={Copy} label="Copy" shortcut={`${MOD}C`} onClick={owner?.copy} disabled={!owner} />
      <Row icon={ClipboardPaste} label="Paste" shortcut={`${MOD}V`} onClick={canvasOwner.paste ?? undefined} disabled={!canvasClip.current || !canvasOwner.paste} />
      <Row icon={CopyPlus} label="Duplicate" shortcut={`${MOD}D`} onClick={owner?.duplicate} disabled={!owner} />
      {owner && <>
        {sep}
        <Row icon={BringToFront} label="Bring to front" shortcut={`${SHIFT}${MOD}]`} onClick={() => owner.order('front')} />
        <Row icon={ArrowUp} label="Bring forward" shortcut={`${MOD}]`} onClick={() => owner.order('forward')} />
        <Row icon={ArrowDown} label="Send backward" shortcut={`${MOD}[`} onClick={() => owner.order('backward')} />
        <Row icon={SendToBack} label="Send to back" shortcut={`${SHIFT}${MOD}[`} onClick={() => owner.order('back')} />
        {sep}
        <div className="px-2 pb-1 pt-0.5 text-[11px] font-medium text-[#a8a29e]">Align to page</div>
        <div className="flex items-center justify-between px-1 pb-1">
          {aligns.map(({ a, icon: I, label, key }) => (
            <button key={a} role="menuitem" onClick={run(() => owner.align(a))} title={`${label} (${ALT}${key})`} aria-label={label} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#44403c] hover:bg-[#f5f5f4]"><I className="h-4 w-4" /></button>
          ))}
        </div>
        {owner.selectGroup && <>{sep}<Row icon={Group} label="Select group" onClick={owner.selectGroup} /></>}
        {sep}
        <Row icon={Trash2} label="Delete" shortcut="⌫" onClick={owner.remove} danger />
      </>}
    </div>,
    document.body,
  );
}
